import { fetchAuthSession } from 'aws-amplify/auth';
import type {
    SupplierControlService,
    ControlSupplier,
    ControlRequest,
    ControlUser,
    OnboardedSupplier,
} from '../types';
import { getAwsConfig } from '../../config/appConfig';
import { ensureAmplifyConfigured } from './amplifyConfig';

/**
 * Supplier Control (internal access management) service.
 *
 * Users, Approval Queue, and the Onboarded Suppliers directory are backed by
 * the control API (Cognito-derived). The per-site access model (organizations,
 * sites, per-login access) needs a data store that isn't built yet, so
 * listSuppliers returns empty and setSiteAccess is a no-op until that follow-up
 * lands.
 */

async function controlUrl(path: string): Promise<string> {
    const base = getAwsConfig().asnApiEndpoint;
    if (!base) {
        throw new Error('ASN API endpoint is not configured.');
    }
    return `${base.replace(/\/$/, '')}/v1/control${path}`;
}

async function authHeader(): Promise<Record<string, string>> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
        throw new Error('Not authenticated.');
    }
    return { Authorization: token };
}

export const awsSupplierControlService: SupplierControlService = {
    async listSuppliers(): Promise<ControlSupplier[]> {
        // Per-site access model (sites + per-login access) is a separate follow-up.
        return [];
    },

    async listOnboardedSuppliers(): Promise<OnboardedSupplier[]> {
        ensureAmplifyConfigured();
        const res = await fetch(await controlUrl('/suppliers'), { headers: await authHeader() });
        if (!res.ok) {
            throw new Error(`Failed to load suppliers: ${res.status} ${res.statusText}`);
        }
        const body = (await res.json()) as { suppliers: OnboardedSupplier[] };
        return body.suppliers;
    },

    async listRequests(): Promise<ControlRequest[]> {
        ensureAmplifyConfigured();
        const res = await fetch(await controlUrl('/requests'), { headers: await authHeader() });
        if (!res.ok) {
            throw new Error(`Failed to load requests: ${res.status} ${res.statusText}`);
        }
        const body = (await res.json()) as { requests: ControlRequest[] };
        return body.requests;
    },

    async approveRequest(requestId: string, teamId?: string): Promise<ControlRequest[]> {
        ensureAmplifyConfigured();
        // Optional team assignment: when the admin provides a teamId, the login
        // joins that team and shares its clientId/secret + reporting partition.
        // Omitted -> the backend makes the login its own team (teamId = sub).
        const trimmed = teamId?.trim();
        const res = await fetch(
            await controlUrl(`/requests/${encodeURIComponent(requestId)}/approve`),
            {
                method: 'POST',
                headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
                body: JSON.stringify(trimmed ? { teamId: trimmed } : {}),
            },
        );
        if (!res.ok) {
            throw new Error(`Failed to approve request: ${res.status} ${res.statusText}`);
        }
        const body = (await res.json()) as { requests: ControlRequest[] };
        return body.requests;
    },

    async listUsers(): Promise<ControlUser[]> {
        ensureAmplifyConfigured();
        const res = await fetch(await controlUrl('/users'), { headers: await authHeader() });
        if (!res.ok) {
            throw new Error(`Failed to load users: ${res.status} ${res.statusText}`);
        }
        const body = (await res.json()) as { users: ControlUser[] };
        return body.users;
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async setSiteAccess(email: string, siteId: string, granted: boolean): Promise<void> {
        // No-op until the suppliers/sites access-management model exists.
    },
};

export default awsSupplierControlService;
